import { Box, CircularProgress, Divider, InputAdornment, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { CampaignKeyword, ExperimentStrategy, MaxCpcMode, useGetCampaignStrategyQuery } from "src/context/api/bidStratTesting/api";
import { COLORS } from "./components";
import MaxCpcStep, { KeywordValues, keywordsOfMode, maxCpcErrors, maxCpcKeywordsPayload } from "./max-cpc-step";
import { tr } from "./i18n";

// Strategies switched with one campaign-level scraping line (lot 1b)
export const EXPERIMENT_STRATEGIES: ExperimentStrategy[] = [
    "Target ROAS",
    "Maximize Conversion Value",
    "Target CPA",
    "Maximize Conversions",
    "Target Impression Share",
];
// Lot 1c: keyword bids only exist on Search campaigns
export const MAX_CPC_STRATEGY: ExperimentStrategy = "Max CPC";

// Values allowed by the scraping.target_impression_share_position constraint
export const TIS_POSITIONS = ["Anywhere on results page", "Top of results page", "Absolute top of results page"];

export interface StrategyParams {
    targetRoas: string;
    targetCpa: string;
    maxCpc: string;
    tis: string;
    position: string;
    // Lot 1c, Max CPC: Signal mode and values per keyword
    mode: MaxCpcMode | "";
    keywordValues: Record<string, KeywordValues>;
}

export const EMPTY_STRATEGY_PARAMS: StrategyParams = {
    targetRoas: "",
    targetCpa: "",
    maxCpc: "",
    tis: "",
    position: "",
    mode: "",
    keywordValues: {},
};

const toNumber = (value: string) => (value.trim() === "" ? null : Number(value));

/**
 * Error per field, empty when the parameters can be sent (same rules as the API).
 * Max CPC: `keywords` are the campaign keywords (GET .../keywords).
 */
export function strategyErrors(
    strategy: ExperimentStrategy | "",
    params: StrategyParams,
    keywords?: CampaignKeyword[]
): Record<string, string> {
    if (!strategy) return {};
    if (strategy === MAX_CPC_STRATEGY) {
        return maxCpcErrors(params.mode, keywordsOfMode(keywords, params.mode), params.keywordValues);
    }
    const errors: Partial<Record<keyof StrategyParams, string>> = {};
    const maxCpc = toNumber(params.maxCpc);
    if (maxCpc === null || Number.isNaN(maxCpc) || maxCpc < 0.01 || maxCpc > 50) errors.maxCpc = tr("strategy_step.between", { min: 0.01, max: 50 });

    // Every parameter is required, the Maximize strategies' target too (Lily, 29/10)
    const roas = toNumber(params.targetRoas);
    if ((strategy === "Target ROAS" || strategy === "Maximize Conversion Value") && roas === null) {
        errors.targetRoas = tr("common.required");
    }
    if (roas !== null && (Number.isNaN(roas) || roas <= 0)) errors.targetRoas = tr("strategy_step.above_zero");

    const cpa = toNumber(params.targetCpa);
    if ((strategy === "Target CPA" || strategy === "Maximize Conversions") && cpa === null) errors.targetCpa = tr("common.required");
    if (cpa !== null && (Number.isNaN(cpa) || cpa < 0.01)) errors.targetCpa = tr("strategy_step.at_least", { min: 0.01 });

    if (strategy === "Target Impression Share") {
        const tis = toNumber(params.tis);
        if (tis === null || Number.isNaN(tis) || tis < 1 || tis > 100) errors.tis = tr("strategy_step.between", { min: 1, max: 100 });
        if (!TIS_POSITIONS.includes(params.position)) errors.position = tr("common.required");
    }
    return errors;
}

/** Body fields of the strategy switch. */
export function strategyPayload(strategy: ExperimentStrategy, params: StrategyParams, keywords?: CampaignKeyword[]) {
    if (strategy === MAX_CPC_STRATEGY && params.mode) {
        return {
            strategy,
            mode: params.mode,
            keywords: maxCpcKeywordsPayload(params.mode, keywordsOfMode(keywords, params.mode), params.keywordValues),
        };
    }
    const isRoas = strategy === "Target ROAS" || strategy === "Maximize Conversion Value";
    const isCpa = strategy === "Target CPA" || strategy === "Maximize Conversions";
    const isTis = strategy === "Target Impression Share";
    return {
        strategy,
        target_roas: isRoas ? toNumber(params.targetRoas) : null,
        target_cpa: isCpa ? toNumber(params.targetCpa) : null,
        max_cpc: toNumber(params.maxCpc),
        tis: isTis ? toNumber(params.tis) : null,
        position: isTis ? params.position : null,
    };
}

const SECTION_LABEL_SX = { fontSize: 13, fontWeight: 500, color: COLORS.secondary, mb: 1 };

interface Props {
    workspaceId: number;
    regionName: string;
    campaignId: string;
    strategy: ExperimentStrategy | "";
    onStrategyChange: (strategy: ExperimentStrategy) => void;
    params: StrategyParams;
    onParamsChange: (params: StrategyParams) => void;
    showErrors: boolean;
    fieldSx: object;
    // Lot 1c: Max CPC is only offered for a Search campaign
    searchCampaign: boolean;
}

/** Modal step 2: current strategy (G-READ), tested strategy and its parameters. */
export default function StrategyStep({
    workspaceId,
    regionName,
    campaignId,
    strategy,
    onStrategyChange,
    params,
    onParamsChange,
    showErrors,
    fieldSx,
    searchCampaign,
}: Props) {
    const { data: current, isFetching, isError } = useGetCampaignStrategyQuery({
        workspace_id: workspaceId,
        region_name: regionName,
        campaign_id: campaignId,
    });
    const errors: Record<string, string> = showErrors && strategy !== MAX_CPC_STRATEGY ? strategyErrors(strategy, params) : {};
    const strategies = searchCampaign ? [...EXPERIMENT_STRATEGIES, MAX_CPC_STRATEGY] : EXPERIMENT_STRATEGIES;

    const field = (key: "targetRoas" | "targetCpa" | "maxCpc" | "tis", label: string, unit?: string) => (
        <TextField
            label={label}
            type="number"
            value={params[key]}
            onChange={(e) => onParamsChange({ ...params, [key]: e.target.value })}
            InputLabelProps={{ shrink: true }}
            InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
            error={!!errors[key]}
            helperText={errors[key]}
            fullWidth
            sx={fieldSx}
        />
    );

    const isRoas = strategy === "Target ROAS" || strategy === "Maximize Conversion Value";
    const isCpa = strategy === "Target CPA" || strategy === "Maximize Conversions";

    return (
        <Stack gap={2.5}>
            <Box>
                <Typography sx={SECTION_LABEL_SX}>{tr("strategy_step.current")}</Typography>
                <Box
                    sx={{
                        border: "1px solid rgba(145, 158, 171, 0.3)",
                        borderRadius: "8px",
                        px: 1.25,
                        py: 1,
                        minHeight: 34,
                        display: "flex",
                        alignItems: "center",
                        fontSize: 14,
                        fontWeight: 600,
                        color: isError ? "error.main" : COLORS.text,
                    }}
                >
                    {isFetching && <CircularProgress size={16} />}
                    {!isFetching && isError && tr("strategy_step.current_error")}
                    {!isFetching && !isError && current?.bidding_strategy_type}
                </Box>
            </Box>

            <Divider sx={{ borderColor: COLORS.border }} />

            <Box>
                <Typography sx={SECTION_LABEL_SX}>{tr("strategy_step.select")}</Typography>
                <TextField
                    select
                    value={strategy}
                    onChange={(e) => onStrategyChange(e.target.value as ExperimentStrategy)}
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) =>
                            value ? (value as string) : <Box component="span" sx={{ color: COLORS.muted }}>{tr("strategy_step.placeholder")}</Box>,
                    }}
                    fullWidth
                    sx={{ ...fieldSx, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 14, fontWeight: 600, color: COLORS.text } }}
                >
                    {strategies.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            {strategy === MAX_CPC_STRATEGY && (
                <MaxCpcStep
                    workspaceId={workspaceId}
                    regionName={regionName}
                    campaignId={campaignId}
                    mode={params.mode}
                    onModeChange={(mode) => onParamsChange({ ...params, mode })}
                    values={params.keywordValues}
                    onValuesChange={(keywordValues) => onParamsChange({ ...params, keywordValues })}
                    showErrors={showErrors}
                />
            )}

            {strategy && strategy !== MAX_CPC_STRATEGY && (
                <Box>
                    <Typography sx={SECTION_LABEL_SX}>{tr("strategy_step.parameters")}</Typography>
                    <Stack direction="row" gap={2} alignItems="flex-start" pt={1}>
                        {isRoas && field("targetRoas", "Target ROAS", "%")}
                        {isCpa && field("targetCpa", "Target CPA")}
                        {strategy === "Target Impression Share" && field("tis", "TIS", "%")}
                        {field("maxCpc", "Max CPC")}
                        {strategy === "Target Impression Share" && (
                            <TextField
                                select
                                label={tr("strategy_step.position")}
                                value={params.position}
                                onChange={(e) => onParamsChange({ ...params, position: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.position}
                                helperText={errors.position}
                                fullWidth
                                sx={fieldSx}
                            >
                                {TIS_POSITIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Stack>
                </Box>
            )}
        </Stack>
    );
}
